/**
 * Personalize CTA functionality for custom card products
 * Handles authentication detection, URL construction, and redirect flows
 */

// Authentication Detection Utility (SRP - max 20 lines)
class AuthenticationDetector {
  static isAuthenticated() {
    try {
      // Defensive programming: validate customer object structure
      if (typeof window.customer === 'undefined') return false;
      if (window.customer === null) return false;

      // Fail fast with meaningful validation
      const hasRequiredProps = window.customer.hasOwnProperty('id') &&
                               window.customer.hasOwnProperty('email');

      if (!hasRequiredProps) {
        console.warn('PersonalizeCTA: Customer object missing required properties');
        return false;
      }

      return Boolean(window.customer.id);
    } catch (error) {
      console.error('PersonalizeCTA: Authentication check failed:', error);
      return false;
    }
  }
}

// URL Construction Utility with Design by Contract (max 40 lines)
class PersonalizeURLBuilder {
  static isStaging() {
    // Detect staging environment by hostname
    const hostname = window.location.hostname;
    return hostname.includes('myshopify.com') || hostname.includes('staging');
  }

  static getBaseURL() {
    // Use localhost:3000 in staging, production URL otherwise
    return this.isStaging()
      ? 'http://localhost:3000/editor'
      : 'https://app.momentdeck.com/editor';
  }

  static buildDesignerURL(productData) {
    // Precondition: valid variant ID required
    if (!productData?.variantId) {
      throw new Error('PersonalizeCTA: Variant ID is required');
    }

    try {
      // Input sanitization and validation
      const variantId = parseInt(productData.variantId, 10);

      // Validate sanitized data
      if (isNaN(variantId)) {
        throw new Error('PersonalizeCTA: Invalid variant ID');
      }

      // Construct designer app URL with environment-aware base
      const baseURL = this.getBaseURL();
      const params = new URLSearchParams({
        'variant-id': variantId.toString()
      });

      const designerURL = `${baseURL}?${params.toString()}`;

      // Postcondition: properly encoded, validated URL returned
      const expectedPrefix = this.isStaging() ? 'http://localhost:3000/editor' : 'https://app.momentdeck.com/editor';
      if (!designerURL.startsWith(expectedPrefix)) {
        throw new Error('PersonalizeCTA: Invalid designer URL constructed');
      }

      return designerURL;
    } catch (error) {
      console.error('PersonalizeCTA: URL construction failed:', error);
      throw error;
    }
  }
}

// Product Context Handler with High Cohesion (max 30 lines)
class ProductContextExtractor {
  static extractProductData(productFormElement) {
    try {
      // Minimize dependencies on global objects
      if (!productFormElement || !productFormElement.dataset) {
        throw new Error('PersonalizeCTA: Invalid product form element');
      }

      // Extract current product and variant information
      const productData = {
        productId: productFormElement.dataset.productId,
        productHandle: productFormElement.dataset.productHandle,
        variantId: productFormElement.dataset.variantId,
        isPersonalizable: productFormElement.dataset.isPersonalizable === 'true'
      };

      // Handle multiple variants and product options
      const variantIdInput = productFormElement.querySelector('.product-variant-id');
      if (variantIdInput && variantIdInput.value) {
        productData.variantId = variantIdInput.value;
      }

      // Format product data for URL parameters (explicit interface)
      return {
        productId: productData.productId,
        productHandle: productData.productHandle,
        variantId: productData.variantId,
        isPersonalizable: productData.isPersonalizable
      };
    } catch (error) {
      console.error('PersonalizeCTA: Product context extraction failed:', error);
      return null;
    }
  }
}

// Session Management with Graceful Degradation
class SessionManager {
  static getCustomerSession() {
    try {
      // Continue with reduced functionality if session unavailable
      if (!window.customer) {
        return { isValid: false, fallbackState: 'guest_mode' };
      }

      // Leverage existing Shopify customer session
      return {
        isValid: true,
        customerId: window.customer.id,
        customerEmail: window.customer.email,
        lastActivity: Date.now()
      };
    } catch (error) {
      console.error('PersonalizeCTA: Session retrieval failed:', error);
      // Implement meaningful fallback states
      return { isValid: false, fallbackState: 'session_error' };
    }
  }

  static handleSessionTimeout(callback) {
    // Add timeout handling with user feedback
    const session = this.getCustomerSession();
    if (!session.isValid) {
      if (callback) callback('session_timeout');
      return false;
    }
    return true;
  }
}

// Orthogonal CTA Click Handler (max 50 lines)
class PersonalizeCTAHandler {
  constructor() {
    this.init();
  }

  init() {
    // Add event listener to personalize CTA buttons
    this.attachEventListeners();
  }

  attachEventListeners() {
    // Separate event handling from authentication logic
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.personalize-cta')) return;

      event.preventDefault(); // Prevent default button behavior
      this.handlePersonalizeCTAClick(event.target.closest('.personalize-cta'));
    });
  }

  handlePersonalizeCTAClick(ctaButton) {
    try {
      const productForm = ctaButton.closest('product-form');
      if (!productForm) throw new Error('PersonalizeCTA: Product form not found');

      // Extract product context
      const productData = ProductContextExtractor.extractProductData(productForm);
      if (!productData) throw new Error('PersonalizeCTA: Failed to extract product data');

      // Implement different flows for authenticated vs unauthenticated users
      if (AuthenticationDetector.isAuthenticated()) {
        this.handleAuthenticatedFlow(productData);
      } else {
        this.handleUnauthenticatedFlow(productData);
      }
    } catch (error) {
      this.handleError(error, ctaButton);
    }
  }

  handleAuthenticatedFlow(productData) {
    // Apply composition over inheritance principles
    const designerURL = PersonalizeURLBuilder.buildDesignerURL(productData);
    window.location.href = designerURL;
  }

  handleUnauthenticatedFlow(productData) {
    // Redirect directly to designer app - it handles its own authentication
    const designerURL = PersonalizeURLBuilder.buildDesignerURL(productData);
    window.location.href = designerURL;
  }

  // Comprehensive Error Handling with Exception Safety
  handleError(error, ctaButton) {
    console.error('PersonalizeCTA: Operation failed:', error);

    // Operations succeed completely or fail cleanly
    if (ctaButton) {
      ctaButton.disabled = false;
      ctaButton.classList.remove('loading');
    }

    // Provide user feedback for failed operations
    this.showUserFeedback('Something went wrong. Please try again.', 'error');

    // Graceful degradation for JavaScript-disabled browsers
    if (error.name === 'NetworkError') {
      this.showUserFeedback('Network error. Please check your connection.', 'warning');
    }
  }

  showUserFeedback(message, type = 'info') {
    // Simple user feedback implementation
    const notification = document.createElement('div');
    notification.className = `personalize-notification personalize-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 9999;
      padding: 12px 16px; border-radius: 4px; color: white;
      background: ${type === 'error' ? '#dc2626' : type === 'warning' ? '#d97706' : '#059669'};
    `;

    document.body.appendChild(notification);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PersonalizeCTAHandler();
  });
} else {
  new PersonalizeCTAHandler();
}